// @ts-strict-ignore
import * as React from 'react';
import { ReleaseTag, ReleaseTagState, RunwayResponseType, SelectableCommit } from '../types';
import {
    releaseStepDetailsContainerModuleBackgroundStyle,
    releaseStepDetailsContainerModuleStyle,
    releaseStepModuleHeaderStyle,
    releaseStepModuleTitleStyle,
    runwayColors,
} from './styles/globalStyles';
import { getTagStateColor } from './stepRunSummaries/archetypeSpecificStepRunSummaryPanels';
import { InfoCircleOutlined, CheckOutlined, LinkOutlined, EditOutlined } from '@ant-design/icons';
import Avatar from 'antd/lib/avatar/avatar';
import { TagReleaseRequestReleaseStep, tryUpdateRelease } from '../actions/release';
import { ReleaseUpdatable } from '../actions/updatable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { getError, getIsLoading, getUpdated } from '../helpers';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import Form, { useForm } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import AutomatedByRunwayIcon from './AutomatedByRunwayIcon';
import Input from 'antd/lib/input';
import { globalMessage } from '../utils/globalMessage';
import * as API from '../api';
import { selectAuthCredentials } from '../selectors/selectors';
import { CommitSelector } from './CommitSelectorComponent';
import { StepRunUpdatable } from '@/actions/stepRun';
import { useCallUpdatable } from '@/utils/hooks/helpers';

const styles: Record<string, React.CSSProperties> = {
    contentStyle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 1,
        flexWrap: 'wrap',
    },
};

interface AutomatedTakeoffTasksProps extends ReleaseComponentProps {
    releaseTagState: ReleaseTagState;
    predictedTagName?: string;
    releaseTag?: ReleaseTag;
    userId: string;
    codeDiffUrl?: string;
    releaseVersion: string;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

interface CreateUpdateTagComponentProps {
    releaseId: string;
    taggableCommits: SelectableCommit[];
    diffUrl?: string;
    isUpdate: boolean;
    taggedCommitHash?: string;
    isTagNameUnresolved: boolean;
    loading: boolean;
    onSubmit: (hash, tagName?) => void;
}

const CreateUpdateTagComponent = (props: CreateUpdateTagComponentProps) => {
    const [form] = useForm();

    const onSubmit = () => {
        const commitHash = form.getFieldValue('commitHash');
        const tagName = form.getFieldValue('tagName');

        props.onSubmit(commitHash, tagName);
    };

    const title = props.isUpdate ? 'Update tag commit' : 'Choose a commit to tag';
    const buttonText = props.isUpdate ? 'Update tag' : 'Create tag';
    const headCommitHash: string = props.taggableCommits[0]?.hash;
    const previouslyTaggedHash = props.taggedCommitHash ?? null;
    let initialValue: string = headCommitHash;

    if (previouslyTaggedHash && previouslyTaggedHash === headCommitHash) {
        initialValue = null;
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexGrow: 1,
                marginTop: 24,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', width: '90%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', marginRight: 8, flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexGrow: 1 }}>
                        <Text strong>{title}</Text>
                        {props.diffUrl && (
                            <Link href={props.diffUrl} target="_blank">
                                View full diff list
                            </Link>
                        )}
                    </div>
                    <Form form={form} initialValues={{ commitHash: initialValue }} onFinish={onSubmit}>
                        <div>
                            <FormItem noStyle name="commitHash">
                                <CommitSelector
                                    commits={props.taggableCommits}
                                    loading={props.loading}
                                    disabled={props.loading}
                                    disableMatchingHash={previouslyTaggedHash}
                                    style={{
                                        width: '100%',
                                        marginTop: 8,
                                        marginBottom: props.isTagNameUnresolved ? 8 : 0,
                                    }}
                                />
                            </FormItem>
                            {props.isTagNameUnresolved && (
                                <FormItem noStyle name="tagName" rules={[{ required: true }]}>
                                    <Input placeholder="Tag name" autoComplete="off" />
                                </FormItem>
                            )}
                        </div>
                    </Form>
                </div>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UATagCommit}
                    releaseId={props.releaseId}
                    type="primary"
                    onClick={() => {
                        form.submit();
                    }}
                    disabled={props.loading}
                >
                    {buttonText}
                </PermissionedActionButton>
            </div>
        </div>
    );
};

const AutomatedTakeoffTasks: React.FC<AutomatedTakeoffTasksProps> = (props: AutomatedTakeoffTasksProps) => {
    return (
        <div
            style={{
                ...releaseStepDetailsContainerModuleStyle,
                ...releaseStepDetailsContainerModuleBackgroundStyle,
            }}
        >
            <div style={{ ...releaseStepModuleHeaderStyle }}>
                <Space>
                    <Text style={{ ...releaseStepModuleTitleStyle }}>Automated release tasks</Text>
                    <Tooltip title="These tasks will be automatically performed by Runway once your app is released.">
                        <InfoCircleOutlined style={{ color: runwayColors.info }} />
                    </Tooltip>
                </Space>
            </div>
            <div
                style={{
                    marginTop: 18,
                }}
            >
                <AutomatedTakeoffTasksBase {...props} />
            </div>
        </div>
    );
};

export const CreatedTagInfo: React.FC<{
    releaseTag?: ReleaseTag;
    predictedTagName?: string;
}> = ({ releaseTag, predictedTagName }) => {
    if (!releaseTag) {
        return null;
    }

    const taggedColor = getTagStateColor(ReleaseTagState.Tagged);

    return (
        releaseTag.isAuthorRunway && (
            <Space size={'middle'} style={{ flexWrap: 'wrap' }}>
                <CheckOutlined style={{ color: taggedColor }} />
                <Space size={'small'}>
                    <Text style={{ flexShrink: 0 }}>Tagged</Text>
                    {predictedTagName && <Text code>{predictedTagName}</Text>}
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', flexShrink: 1 }}>
                        <Button icon={<LinkOutlined />} type="link" href={releaseTag.url} target="_blank" />
                        <Text ellipsis style={{ maxWidth: 200 }}>
                            - (
                            <Link href={releaseTag.commitURL} target="_blank">
                                {releaseTag.commitHash.substring(0, 7)}
                            </Link>
                            ) {releaseTag.commitMessage}
                        </Text>
                    </div>
                </Space>
            </Space>
        )
    );
};

export const AutomatedTakeoffTasksBase: React.FC<AutomatedTakeoffTasksProps> = (props: AutomatedTakeoffTasksProps) => {
    const [showUpdateTagComponent, setShowUpdateTagComponent] = React.useState(false);
    const [taggableCommits, setTaggableCommits] = React.useState([]);
    const [fetchCommitsError, setFetchCommitsError] = React.useState(null);

    const [tagReleaseStepRun, tagReleaseIsLoadingStepRun, tagReleaseResponseTypeStepRun, tagReleaseErrorStepRun] =
        useCallUpdatable(StepRunUpdatable.TagRelease, props.stepRunComponentProps);

    const createTagIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.TagRelease, state, props.releaseId),
    );
    const createTagErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.TagRelease, state, props.releaseId),
    );
    const updateTagIsLoadingReleaseStep = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.UpdateReleaseTag, state, props.releaseId),
    );
    const updateTagSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.UpdateReleaseTag, state, props.releaseId),
    );
    const updateTagErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.UpdateReleaseTag, state, props.releaseId),
    );

    const createTagIsLoading = props.stepRunComponentProps ? tagReleaseIsLoadingStepRun : createTagIsLoadingReleaseStep;
    const createTagError = props.stepRunComponentProps ? tagReleaseErrorStepRun : createTagErrorReleaseStep;
    const updateTagIsLoading = props.stepRunComponentProps ? tagReleaseIsLoadingStepRun : updateTagIsLoadingReleaseStep;
    const updateTagSuccess =
        props.stepRunComponentProps ?
            tagReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   updateTagSuccessReleaseStep;
    const updateTagError = props.stepRunComponentProps ? tagReleaseErrorStepRun : updateTagErrorReleaseStep;

    const authCreds = useSelector((state: RootState) => selectAuthCredentials(state));

    const dispatch = useDispatch();

    const releaseTagItem = (releaseTagState: ReleaseTagState, predictedTagName?: string, releaseTag?: ReleaseTag) => {
        const normalTagInfo = (predictedTagName?: string) => {
            return (
                <Space>
                    <Text>◦</Text>
                    <Text>Create release tag</Text>
                    {predictedTagName && <Text code>{predictedTagName}</Text>}
                </Space>
            );
        };

        if (releaseTagState === ReleaseTagState.NotTaggable) {
            return (
                <div style={styles.contentStyle}>
                    {normalTagInfo(props.predictedTagName)}
                    <div>
                        <Text style={{ color: getTagStateColor(releaseTagState) }} strong>
                            Pending release
                        </Text>
                    </div>
                </div>
            );
        } else if (releaseTagState === ReleaseTagState.Tagged && releaseTag) {
            const taggedColor = getTagStateColor(releaseTagState);
            return (
                <div style={styles.contentStyle}>
                    <Space
                        size={'middle'}
                        style={releaseTag.isAuthorRunway ? { flexWrap: 'wrap' } : { flexWrap: 'wrap', opacity: 0.33 }}
                    >
                        {releaseTag.isAuthorRunway ?
                            <CheckOutlined style={{ color: taggedColor }} />
                        :   <Text>◦</Text>}
                        <Space size={'small'}>
                            {releaseTag.isAuthorRunway && <AutomatedByRunwayIcon />}
                            <Text style={{ flexShrink: 0 }}>
                                {releaseTag.isAuthorRunway ? 'Created' : 'Create'} release tag
                            </Text>
                            {predictedTagName && <Text code>{predictedTagName}</Text>}
                            {releaseTag.isAuthorRunway && (
                                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', flexShrink: 1 }}>
                                    <Button icon={<LinkOutlined />} type="link" href={releaseTag.url} target="_blank" />
                                    <Text ellipsis style={{ maxWidth: 200 }}>
                                        - (
                                        <Link href={releaseTag.commitURL} target="_blank">
                                            {releaseTag.commitHash.substring(0, 7)}
                                        </Link>
                                        ) {releaseTag.commitMessage}
                                    </Text>
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            const prevShowUpdateTagComponent = showUpdateTagComponent;

                                            setShowUpdateTagComponent(!prevShowUpdateTagComponent);
                                        }}
                                    />
                                </div>
                            )}
                        </Space>
                    </Space>
                    {!releaseTag.isAuthorRunway && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Button icon={<LinkOutlined />} type="link" href={releaseTag.url} target="_blank" />
                            <Text style={{ color: taggedColor, marginRight: 8 }} strong>
                                Already tagged
                            </Text>
                            {releaseTag.authorName && (
                                <Tooltip title={releaseTag.authorName}>
                                    <Avatar>{releaseTag.authorName.charAt(0).toUpperCase()}</Avatar>
                                </Tooltip>
                            )}
                        </div>
                    )}
                    {releaseTag.isAuthorRunway && (
                        <div>
                            <Text style={{ color: taggedColor }} strong>
                                Done
                            </Text>
                        </div>
                    )}
                </div>
            );
        } else if (
            releaseTagState === ReleaseTagState.TagNeeded ||
            releaseTagState === ReleaseTagState.TagNeededNameUnresolved
        ) {
            return (
                <div style={styles.contentStyle}>
                    {normalTagInfo(predictedTagName)}
                    <div>
                        <Text style={{ color: getTagStateColor(releaseTagState) }} strong>
                            Manual selection required
                        </Text>
                    </div>
                </div>
            );
        }

        return null;
    };

    const createTag = (commitHash: string, tagName?: string) => {
        const request: TagReleaseRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            commitHash,
            tagName,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.TagRelease, request, props.releaseId));
    };

    const updateTag = (commitHash: string, tagName?: string) => {
        const request: TagReleaseRequestReleaseStep = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            commitHash,
            tagName,
        };

        if (props.stepRunComponentProps) {
            tagReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateReleaseTag, request, props.releaseId));
        }
    };

    const needsManualSelection =
        props.releaseTagState === ReleaseTagState.TagNeeded ||
        props.releaseTagState === ReleaseTagState.TagNeededNameUnresolved;

    React.useEffect(() => {
        if (updateTagSuccess) {
            setShowUpdateTagComponent(false);
        }
    }, [updateTagSuccess]);

    React.useEffect(() => {
        if (createTagError) {
            globalMessage.error(createTagError);
        }
    }, [createTagError]);

    React.useEffect(() => {
        if (updateTagError) {
            globalMessage.error(updateTagError);
        }
    }, [updateTagError]);

    React.useEffect(() => {
        API.getCandidateSelectableCommits(
            { appId: props.appId, orgId: props.orgId, version: props.releaseVersion },
            authCreds,
        )
            .then((response) => {
                setTaggableCommits(response.commits);
            })
            .catch((error) => {
                setFetchCommitsError(error.message);
            });
    }, []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            {releaseTagItem(props.releaseTagState, props.predictedTagName, props.releaseTag)}
            {(needsManualSelection || showUpdateTagComponent) && fetchCommitsError && (
                <Text style={{ color: runwayColors.statusRed, marginTop: 18 }}>{fetchCommitsError}</Text>
            )}
            {needsManualSelection && !showUpdateTagComponent && (
                <CreateUpdateTagComponent
                    releaseId={props.releaseId}
                    taggableCommits={taggableCommits}
                    isUpdate={false}
                    diffUrl={props.codeDiffUrl}
                    isTagNameUnresolved={props.releaseTagState === ReleaseTagState.TagNeededNameUnresolved}
                    loading={createTagIsLoading}
                    onSubmit={createTag}
                />
            )}
            {showUpdateTagComponent && !needsManualSelection && (
                <CreateUpdateTagComponent
                    releaseId={props.releaseId}
                    taggableCommits={taggableCommits}
                    isUpdate={true}
                    taggedCommitHash={props.releaseTag?.commitHash}
                    diffUrl={props.codeDiffUrl}
                    isTagNameUnresolved={props.releaseTagState === ReleaseTagState.TagNeededNameUnresolved}
                    loading={updateTagIsLoading}
                    onSubmit={updateTag}
                />
            )}
        </div>
    );
};

export default AutomatedTakeoffTasks;
